import {useEffect, useState, type ReactNode} from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Link,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {QRCodeSVG} from 'qrcode.react';
import dayjs from 'dayjs';
import {IntercomDevice, IntercomDeviceStatusEnum} from 'src/apis/autoGenerate/types/intercomDevice';
import {intercomDeviceServiceDetail} from 'src/apis/autoGenerate/services';
import {StatusBadge, getDeviceStatusVariant} from 'src/components/ui';
import {formatDate, getVal} from 'src/utils/common';
import {getDeviceBuildingOrAmenityName} from 'src/utils/intercomDevice';
import {neutral} from 'src/theme';

type ViewDeviceDrawerProps = {
  open: boolean;
  device: IntercomDevice | null;
  onClose: () => void;
};

function statusLabel(status?: string) {
  if (status === IntercomDeviceStatusEnum.Online) return 'Online';
  if (status === IntercomDeviceStatusEnum.Offline) return 'Offline';
  return 'Unknown';
}

function deviceTypeLabel(device: IntercomDevice) {
  if (device.deviceType === 'VIRTUAL') return 'QR';
  if (device.deviceType === 'PHYSICAL') return 'Physical';
  return getVal(device.deviceType);
}

function accessGroupLabel(device: IntercomDevice) {
  const names = (device.accessGroup || [])
    .map(group => group.name)
    .filter((name): name is string => Boolean(name));
  const unique = Array.from(new Set(names));
  return unique.length ? unique.join(', ') : '--';
}

function DetailRow({label, value}: {label: string; value: ReactNode}) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        py: 1.25,
        borderBottom: `1px solid ${neutral[200]}`,
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{flexShrink: 0}}>
        {label}
      </Typography>
      <Box sx={{textAlign: 'right', minWidth: 0}}>{value}</Box>
    </Box>
  );
}

export default function ViewDeviceDrawer({open, device, onClose}: ViewDeviceDrawerProps) {
  const [detail, setDetail] = useState<IntercomDevice | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!open || !device?.id) {
      setDetail(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadFailed(false);
    intercomDeviceServiceDetail(device.id)
      .then(res => {
        if (!cancelled) setDetail(res.data);
      })
      .catch(() => {
        if (!cancelled) {
          setDetail(null);
          setLoadFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, device?.id]);

  const shown = device
    ? {
        ...device,
        ...detail,
        deviceType: detail?.deviceType || device.deviceType,
        apartment: detail?.apartment || device.apartment,
        building: detail?.building || device.building,
        accessGroup: detail?.accessGroup || device.accessGroup,
      }
    : null;
  const isVirtual = shown?.deviceType === 'VIRTUAL';
  const qrUrl = detail?.qrUrl;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{sx: {width: {xs: '100%', sm: 420}}}}
    >
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 2}}>
        <Box>
          <Typography variant="h6">View Device</Typography>
          {shown?.name && (
            <Typography variant="body2" color="text.secondary">
              {shown.name}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <Box sx={{px: 2.5, py: 2.5, overflow: 'auto'}}>
        {isVirtual && (
          <Box
            sx={{
              bgcolor: neutral[100],
              borderRadius: 2,
              p: 2.5,
              mb: 3,
              textAlign: 'center',
            }}
          >
            <Typography variant="subtitle1" sx={{mb: 2, textAlign: 'left'}}>
              Device QR Code
            </Typography>
            {loading ? (
              <CircularProgress size={28} />
            ) : loadFailed ? (
              <Typography variant="body2" color="text.secondary">
                Could not load the QR link. Close this panel and try again.
              </Typography>
            ) : qrUrl ? (
              <>
                <Box
                  sx={{
                    display: 'inline-flex',
                    bgcolor: '#fff',
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <QRCodeSVG value={qrUrl} size={220} />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{mt: 1.5}}>
                  Generated on:{' '}
                  {detail?.qrGeneratedAt ? dayjs(detail.qrGeneratedAt).format('MM/DD/YYYY') : '--'}
                </Typography>
                <Link
                  href={qrUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 1, fontWeight: 600}}
                >
                  Open QR Link
                  <OpenInNewIcon sx={{fontSize: 16}} />
                </Link>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                This virtual device does not have a QR link yet.
              </Typography>
            )}
          </Box>
        )}

        {shown && (
          <Box>
            <Typography variant="subtitle1" sx={{mb: 1}}>
              Device Details
            </Typography>
            <DetailRow label="Device Type" value={deviceTypeLabel(shown)} />
            <DetailRow
              label="Status"
              value={
                <StatusBadge
                  status={getDeviceStatusVariant(shown.status)}
                  label={statusLabel(shown.status)}
                />
              }
            />
            <DetailRow label="Property" value={getVal(shown.apartment?.name)} />
            <DetailRow
              label="Building / Amenity"
              value={getVal(getDeviceBuildingOrAmenityName(shown))}
            />
            <DetailRow label="Access Group" value={accessGroupLabel(shown)} />
            <DetailRow label="Time Zone" value={getVal(shown.timeZone)} />
            <DetailRow
              label="Installed On"
              value={shown.installedOn ? formatDate(shown.installedOn) : '--'}
            />
            {shown.integration && <DetailRow label="Door Integration" value={shown.integration} />}
            {shown.intercomTimeoutSeconds != null && (
              <DetailRow label="Call Timeout" value={`${shown.intercomTimeoutSeconds}s`} />
            )}
            {shown.locationVerificationEnabled != null && (
              <DetailRow
                label="Location Check"
                value={shown.locationVerificationEnabled ? 'Enabled' : 'Disabled'}
              />
            )}
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
